/**
 * Monoline UI — ro'yxat asboblari.
 *
 * Bu yerda 40+ elementli ro'yxatlarni boshqarish uchun kerak bo'ladigan
 * to'rt narsa va bitta ko'rgich yashaydi. Har biri bitta sahifa uchun emas,
 * UMUMIY yozilgan: shablonlar ham, AI jurnali ham, murojaatlar ham
 * bir xil qidiruv va saralashni oladi — hozir esa har sahifa buni
 * qaytadan yozmoqda.
 *
 *   SearchInput — qidiruv (debounce + tozalash + Esc)
 *   SortMenu    — saralash tanlagichi (jadval TASHQARISIDAGI ro'yxat uchun)
 *   Coverage    — to'ldirilganlik chiplari (qaysi til bor, qaysi maydon to'lgan)
 *   CardActions — kartadagi amallar qatori qoidasi (bitta tugma + menyu)
 *   TextViewer  — uzun matn/prompt ko'rgichi (o'ralgan, skrollanadigan, nusxa)
 *
 * Tashqi bog'liqlik: `react`, `react-icons/hi2` va kitning o'z komponentlari.
 */

import { forwardRef, useEffect, useRef, useState } from 'react'
import {
  HiArrowSmallDown,
  HiArrowSmallUp,
  HiBarsArrowDown,
  HiCheck,
  HiClipboard,
  HiClipboardDocumentCheck,
  HiEllipsisHorizontal,
  HiMagnifyingGlass,
  HiMinus,
  HiXMark,
} from 'react-icons/hi2'

import { Badge, Button, ButtonGroup, Input, cx } from './primitives.jsx'
import { Menu } from './composites.jsx'

/* ─────────────────────────────────────────────────────────────
   SearchInput
   ───────────────────────────────────────────────────────────── */

/**
 * Qidiruv maydoni: ikonka, tozalash tugmasi va debounce.
 *
 * Nima uchun debounce kitda: hozir har sahifa qidiruvni o'zi yozadi va
 * ba'zisi har harfda butun ro'yxatni qayta filtrlaydi. Kechikish bitta
 * joyda turgani ma'qul — shunda "qidiruv tez" degan xulq sahifadan
 * sahifaga o'zgarmaydi.
 *
 * Maydon boshqariladigan (`value`) bo'lib qoladi, lekin YOZISH paytida
 * ichki holat bilan ishlaydi: tashqi qiymat faqat kechikishdan keyin
 * yangilanadi. Tashqaridan boshqa qiymat kelsa (filtr tiklandi, chip
 * o'chirildi) maydon unga darhol ergashadi.
 *
 * @param value      joriy so'rov
 * @param onChange   (matn) — debounce'dan keyin chaqiriladi
 * @param delay      kechikish, ms (standart 200; 0 — kechikishsiz)
 * @param onClear    tozalash tugmasi bosilganda qo'shimcha ish
 * @param size       'md' | 'sm'
 */
export const SearchInput = forwardRef(function SearchInput(
  {
    value = '',
    onChange,
    placeholder = 'Qidirish',
    delay = 200,
    size = 'md',
    onClear,
    disabled = false,
    className,
    ...rest
  },
  ref
) {
  const [text, setText] = useState(value)
  const emitted = useRef(value)      // oxirgi o'zimiz yuborgan qiymat
  const timer = useRef(null)
  const inputRef = useRef(null)

  // Tashqi qiymat o'zgardi — lekin bu bizning emissiyamiz emas.
  // Shart aynan shu: usiz har debounce'dan keyin kursor sakraydi.
  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value
      setText(value)
    }
  }, [value])

  useEffect(() => () => clearTimeout(timer.current), [])

  const emit = (v, now = false) => {
    clearTimeout(timer.current)
    const send = () => {
      emitted.current = v
      onChange?.(v)
    }
    if (now || !delay) send()
    else timer.current = setTimeout(send, delay)
  }

  const onType = (e) => {
    const v = e.target.value
    setText(v)
    emit(v)
  }

  const clear = () => {
    setText('')
    emit('', true)
    onClear?.()
    inputRef.current?.focus()
  }

  const onKeyDown = (e) => {
    // Esc — qidiruvni tozalaydi. Bo'sh maydonda hodisa yuqoriga o'tadi
    // (modal yopilishi kerak bo'lsa xalaqit qilmaymiz).
    if (e.key === 'Escape' && text) {
      e.preventDefault()
      e.stopPropagation()
      clear()
    }
  }

  // Qo'shimcha (suffix) DOIM beriladi, tugma esa faqat matn bo'lganda.
  // Sabab: `ui-input-wrap:has(.ui-input__suffix)` o'ng tomondan joy ochadi —
  // qo'shimcha faqat matn yozilganda qo'shilsa, birinchi harfda butun matn
  // chapga sakrardi.
  const tail = (
    <span className="ui-search__tail">
      {text && (
        <button
          type="button"
          className="ui-search__x"
          onClick={clear}
          disabled={disabled}
          aria-label="Qidiruvni tozalash"
          title="Qidiruvni tozalash (Esc)"
        >
          <HiXMark aria-hidden="true" />
        </button>
      )}
    </span>
  )

  return (
    <Input
      ref={(node) => {
        inputRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      }}
      type="search"
      size={size}
      icon={HiMagnifyingGlass}
      className={cx('ui-search', className)}
      value={text}
      onChange={onType}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={rest['aria-label'] || placeholder}
      suffix={tail}
      {...rest}
    />
  )
})
SearchInput.displayName = 'SearchInput'

/* ─────────────────────────────────────────────────────────────
   SortMenu
   ───────────────────────────────────────────────────────────── */

/**
 * Saralash tanlagichi — `Menu` ustidagi yupqa qobiq.
 *
 * `Table` o'z sarlavhasida saralaydi, lekin KARTOCHKA PANJARASIDA saralash
 * uchun kitda hech narsa yo'q edi. Joriy tartib tugmaning o'zida yozilib
 * turadi (yashirin holat qolmaydi), yo'nalish esa yonidagi ikkinchi
 * tugmada — bitta bosishda teskarisiga o'tadi.
 *
 * @param options      [{value, label}]
 * @param value        joriy maydon
 * @param onChange     (value)
 * @param dir          'asc' | 'desc' — berilmasa yo'nalish tugmasi chiqmaydi
 * @param onDirChange  (dir)
 * @param label        tugma oldidagi so'z (standart "Saralash")
 * @param align        menyu tekislanishi: 'start' | 'end'
 */
export const SortMenu = forwardRef(function SortMenu(
  {
    options = [],
    value,
    onChange,
    dir,
    onDirChange,
    label = 'Saralash',
    align = 'end',
    size = 'sm',
    className,
    ...rest
  },
  ref
) {
  const current = options.find((o) => o.value === value)
  const dirUp = dir === 'asc'

  return (
    <ButtonGroup ref={ref} className={cx('ui-sortmenu', className)} {...rest}>
      <Menu
        align={align}
        trigger={
          <Button variant="secondary" size={size} icon={HiBarsArrowDown}>
            <span className="ui-sortmenu__lbl">{label}:</span>
            {current?.label || '—'}
          </Button>
        }
        items={options.map((o) => ({
          label: o.label,
          icon: o.value === value ? HiCheck : undefined,
          onClick: () => onChange?.(o.value),
        }))}
      />
      {dir != null && (
        <Button
          variant="secondary"
          size={size}
          icon={dirUp ? HiArrowSmallUp : HiArrowSmallDown}
          iconOnly
          aria-label={dirUp ? "O'sish bo'yicha — kamayishga o'tkazish" : "Kamayish bo'yicha — o'sishga o'tkazish"}
          title={dirUp ? "O'sish bo'yicha" : 'Kamayish bo\'yicha'}
          onClick={() => onDirChange?.(dirUp ? 'desc' : 'asc')}
        />
      )}
    </ButtonGroup>
  )
})
SortMenu.displayName = 'SortMenu'

/* ─────────────────────────────────────────────────────────────
   Coverage
   ───────────────────────────────────────────────────────────── */

/**
 * To'ldirilganlik chiplari.
 *
 * Shablonda — "qaysi tilda tanasi bor" (bo'sh bo'lsa bot boshqa tilni
 * yuboradi, ya'ni kirillcha yozgan fuqaroga lotincha ketadi), analitikada —
 * "qaysi maydon to'lgan". Ikkala holatda ham savol bitta: nimadir YETISHMAYDI.
 *
 * Rang yolg'iz qolmaydi (DESIGN.md 8): to'lgani — belgi + `ok` ohangi,
 * bo'shi — chizilgan belgi + so'nik neytral.
 *
 * @param items    [{key, label, filled, title}]
 * @param label    guruh nomi (ekran o'quvchi uchun)
 * @param onSelect (key, item) — berilsa chiplar bosiladigan bo'ladi
 */
export const Coverage = forwardRef(function Coverage(
  { items = [], label = "To'ldirilganlik", onSelect, className, ...rest },
  ref
) {
  return (
    <span
      ref={ref}
      className={cx('ui-coverage', className)}
      role="group"
      aria-label={label}
      {...rest}
    >
      {items.map((it) => {
        const text = it.title || `${it.label} — ${it.filled ? "to'ldirilgan" : "bo'sh"}`
        const chip = (
          <Badge
            tone={it.filled ? 'ok' : 'neutral'}
            icon={it.filled ? HiCheck : HiMinus}
            className={cx('ui-coverage__chip', !it.filled && 'is-off')}
          >
            {it.label}
          </Badge>
        )
        if (!onSelect) {
          return (
            <span key={it.key} title={text}>
              <span className="ui-sr-only">{text}</span>
              <span aria-hidden="true">{chip}</span>
            </span>
          )
        }
        return (
          <button
            key={it.key}
            type="button"
            className="ui-coverage__btn"
            title={text}
            aria-label={text}
            onClick={() => onSelect(it.key, it)}
          >
            <span aria-hidden="true">{chip}</span>
          </button>
        )
      })}
    </span>
  )
})
Coverage.displayName = 'Coverage'

/* ─────────────────────────────────────────────────────────────
   CardActions
   ───────────────────────────────────────────────────────────── */

/**
 * Kartadagi amallar qatori — komponent ko'rinishidagi QOIDA.
 *
 * Muammo: 300px kartada to'rt tugma qatori (asosiy amal + nusxa + tahrir +
 * o'chirish) siqilib ketadi va eng xavflisi birlamchi amal bilan yonma-yon,
 * bir og'irlikda turadi — tasodifiy bosish oson. Shuning uchun bu yerda
 * shakl qat'iy: KO'RINADIGAN bitta (ko'pi bilan ikkita) tugma, qolgani —
 * menyu ichida, xavflisi esa ajratgichdan keyin.
 *
 * @param primary    {label, icon, onClick, variant='primary', loading, disabled, title}
 * @param secondary  ixtiyoriy ikkinchi tugma (o'sha shakl, standart variant='secondary')
 * @param menu       [{label, icon, onClick, danger, disabled, sep}] — `Menu` items
 * @param menuLabel  menyu tugmasining aria-label i
 * @param align      'end' (standart) | 'start' | 'between'
 */
export const CardActions = forwardRef(function CardActions(
  {
    primary,
    secondary,
    menu = [],
    menuLabel = 'Yana amallar',
    align = 'end',
    size = 'sm',
    className,
    children,
    ...rest
  },
  ref
) {
  const btn = (a, fallbackVariant) =>
    a && (
      <Button
        variant={a.variant || fallbackVariant}
        size={size}
        icon={a.icon}
        loading={a.loading}
        disabled={a.disabled}
        title={a.title}
        aria-label={a.ariaLabel}
        onClick={a.onClick}
      >
        {a.label}
      </Button>
    )

  return (
    <div
      ref={ref}
      className={cx('ui-card-actions', `ui-card-actions--${align}`, className)}
      {...rest}
    >
      {children}
      {btn(secondary, 'secondary')}
      {btn(primary, 'primary')}
      {menu.length > 0 && (
        <Menu
          align="end"
          items={menu}
          trigger={
            <Button
              variant="ghost"
              size={size}
              icon={HiEllipsisHorizontal}
              iconOnly
              aria-label={menuLabel}
              title={menuLabel}
            />
          }
        />
      )}
    </div>
  )
})
CardActions.displayName = 'CardActions'

/* ─────────────────────────────────────────────────────────────
   TextViewer
   ───────────────────────────────────────────────────────────── */

/** Clipboard — `navigator.clipboard` bo'lmagan/ruxsatsiz holat uchun zaxira yo'l. */
async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch { /* pastdagi zaxira yo'l */ }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

/**
 * Uzun matn ko'rgichi: AI prompti, model javobi, xato izi, JSON.
 *
 * Uchta talab bir joyda: satr KO'CHADI (gorizontal skroll yo'q — uzun
 * prompt o'ngga cho'zilib ketmaydi), balandlik CHEKLANGAN (blok sahifani
 * egallab olmaydi), va matn NUSXA olinadi (jurnal yozuvini boshqa joyga
 * ko'chirish — kundalik ish).
 *
 * @param value      matn
 * @param label      sarlavha (chapda)
 * @param meta       sarlavha yonidagi qo'shimcha yozuv; berilmasa qator soni
 * @param maxHeight  eng katta balandlik, px (standart 260); 0 — cheklovsiz
 * @param mono       monoshirift (standart true)
 * @param wrap       satr ko'chirish (standart true)
 * @param copy       nusxalash tugmasi (standart true)
 */
export const TextViewer = forwardRef(function TextViewer(
  {
    value = '',
    label,
    meta,
    maxHeight = 260,
    mono = true,
    wrap = true,
    copy = true,
    emptyText = '—',
    className,
    children,
    ...rest
  },
  ref
) {
  const [state, setState] = useState('idle')
  const timer = useRef(null)
  useEffect(() => () => clearTimeout(timer.current), [])

  const text = String(value ?? '')
  const onCopy = async () => {
    const ok = await copyToClipboard(text)
    setState(ok ? 'done' : 'fail')
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setState('idle'), 1800)
  }

  const lines = text ? text.split('\n').length : 0
  const info = meta ?? (text ? `${lines} qator · ${text.length} belgi` : '')

  return (
    <div ref={ref} className={cx('ui-textview', className)} {...rest}>
      {(label || info || copy) && (
        <div className="ui-textview__hdr">
          {label && <span className="ui-textview__lbl">{label}</span>}
          {info && <span className="ui-textview__meta ui-nums">{info}</span>}
          <span className="ui-grow" />
          {copy && (
            <Button
              variant="ghost"
              size="sm"
              icon={state === 'done' ? HiClipboardDocumentCheck : HiClipboard}
              disabled={!text}
              onClick={onCopy}
            >
              {state === 'done' ? 'Nusxalandi' : state === 'fail' ? 'Nusxalanmadi' : 'Nusxalash'}
            </Button>
          )}
        </div>
      )}
      {/* tabIndex — uzun matn klaviatura bilan ham skroll bo'lishi uchun */}
      <div
        className={cx('ui-textview__body', mono && 'ui-mono', !wrap && 'is-nowrap')}
        style={maxHeight ? { maxHeight } : undefined}
        tabIndex={0}
        role="region"
        aria-label={label || 'Matn'}
      >
        {children ?? (text || <span className="ui-muted">{emptyText}</span>)}
      </div>
    </div>
  )
})
TextViewer.displayName = 'TextViewer'
